import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Flame, Trophy, Star, Award, TrendingUp, Users } from 'lucide-react';
import confetti from 'canvas-confetti';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from 'recharts';

export const GamificationDashboard = () => {
  const [streak, setStreak] = useState(0);
  const [recommendations, setRecommendations] = useState(0);

  // Simulated streak counter animation
  useEffect(() => {
    const targetStreak = 47;
    const interval = setInterval(() => {
      setStreak(prev => {
        if (prev < targetStreak) return prev + 1;
        clearInterval(interval);
        return prev;
      });
    }, 30);
    return () => clearInterval(interval);
  }, []);

  // Simulated recommendations counter
  useEffect(() => {
    const targetRec = 12;
    const interval = setInterval(() => {
      setRecommendations(prev => {
        if (prev < targetRec) return prev + 1;
        clearInterval(interval);
        return prev;
      });
    }, 50);
    return () => clearInterval(interval);
  }, []);

  // Confetti celebration (simulated for 365 days)
  const triggerConfetti = () => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#ff0062', '#7928CA', '#00DFFD']
    });
  };

  // RPG Attributes Data
  const attributesData = [
    { attribute: 'Engajamento', value: 85, fullMark: 100 },
    { attribute: 'Autoridade', value: 72, fullMark: 100 },
    { attribute: 'Contribuição', value: 90, fullMark: 100 },
    { attribute: 'Presença', value: 68, fullMark: 100 },
    { attribute: 'Networking', value: 78, fullMark: 100 }
  ];

  // Prestige Badges
  const badges = [
    { id: 1, name: 'Embaixador', color: 'primary', icon: Star, unlocked: true, level: 'Elite' },
    { id: 2, name: 'Ouro', color: 'warning', icon: Trophy, unlocked: true, level: 'Premium' },
    { id: 3, name: 'Prata', color: 'muted', icon: Award, unlocked: true, level: 'Advanced' },
    { id: 4, name: 'Bronze', color: 'destructive', icon: TrendingUp, unlocked: false, level: 'Starter' }
  ];

  return (
    <section id="gamification" className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="container mx-auto max-w-6xl">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            <span className="gradient-text">Perfil do Jogador</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-base sm:text-lg">
            Evolua suas habilidades e desbloqueie conquistas exclusivas
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Radar Chart - RPG Attributes */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Card className="glass-strong p-6 h-full">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                Atributos de Engajamento de Comunidade
              </h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={attributesData}>
                    <PolarGrid stroke="hsl(var(--border))" />
                    <PolarAngleAxis 
                      dataKey="attribute" 
                      tick={{ fill: 'hsl(var(--foreground))', fontSize: 12 }}
                    />
                    <PolarRadiusAxis 
                      angle={90} 
                      domain={[0, 100]}
                      tick={{ fill: 'hsl(var(--muted-foreground))' }}
                    />
                    <Radar 
                      name="Atributos" 
                      dataKey="value" 
                      stroke="hsl(var(--primary))" 
                      fill="hsl(var(--primary))" 
                      fillOpacity={0.6}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </motion.div>

          {/* Stats & Badges */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            {/* Streak Counter */}
            <Card className="glass-strong p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Flame className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">Sequência Ativa</h3>
                    <p className="text-sm text-muted-foreground">Dias consecutivos</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-primary">{streak}</div>
                  <button 
                    onClick={triggerConfetti}
                    className="text-xs text-muted-foreground hover:text-primary transition-colors"
                  >
                    Simular 365 dias
                  </button>
                </div>
              </div>
              <Progress value={(streak / 365) * 100} className="h-2" />
              <p className="text-xs text-muted-foreground mt-2">Continue ativo para desbloquear recompensas!</p>
            </Card>

            {/* Recommendations System */}
            <Card className="glass-strong p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                    <Users className="h-6 w-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">Recomendações</h3>
                    <p className="text-sm text-muted-foreground">Membros indicados</p>
                  </div>
                </div>
                <div className="text-3xl font-bold gradient-text">{recommendations}</div>
              </div>
              <div className="flex gap-2">
                {[...Array(5)].map((_, i) => (
                  <div 
                    key={i} 
                    className={`flex-1 h-2 rounded-full ${
                      i < Math.floor(recommendations / 3) ? 'bg-primary' : 'bg-muted'
                    }`}
                  />
                ))}
              </div>
            </Card>

            {/* Prestige Badges */}
            <Card className="glass-strong p-6">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Trophy className="h-5 w-5 text-warning" />
                Badges de Prestígio
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {badges.map((badge) => {
                  const Icon = badge.icon;
                  return (
                    <motion.div
                      key={badge.id}
                      whileHover={{ scale: badge.unlocked ? 1.05 : 1 }}
                      className={`glass p-4 rounded-lg text-center transition-all ${
                        badge.unlocked ? 'glow-hover cursor-pointer' : 'opacity-50'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-full bg-${badge.color}/10 flex items-center justify-center mx-auto mb-2 ${
                        badge.unlocked ? 'animate-shimmer' : ''
                      }`}>
                        <Icon className={`h-6 w-6 text-${badge.color}`} />
                      </div>
                      <p className="text-sm font-bold">{badge.name}</p>
                      <p className="text-xs text-muted-foreground">{badge.level}</p>
                      {!badge.unlocked && (
                        <Badge variant="outline" className="mt-2 text-xs">Bloqueado</Badge>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default GamificationDashboard;